/**
 * The Operator Admin frame: a dark top bar with the brand, the three
 * sections (Assignments · Drivers & vehicles · Payments) and who is signed
 * in, then the section itself (`<Outlet />`).
 *
 * Rendered inside PortalRoute → PortalShell, so the portal's tokens, fonts
 * and guard all apply. Its route asks the shell to be `wide` (PortalRoutes.jsx),
 * so the frame gets a desktop layout instead of the driver's phone column.
 */
import React from 'react';
import { NavLink, Outlet, useLocation } from 'react-router-dom';
import BrandLogo from '../Components/BrandLogo';
import { initials } from '../portalFormat';
import signOutOfPortal from '../signOutOfPortal';
import usePortalPrincipal from './usePortalPrincipal';
import './Operator.scss';

const OPERATOR_SECTIONS = [
  // A trip's details page belongs to Assignments, where it is opened from.
  {
    to: '/portal/operator', label: 'Assignments', end: true, alsoActiveUnder: '/portal/operator/trip/',
  },
  { to: '/portal/operator/fleet', label: 'Drivers & vehicles' },
  { to: '/portal/operator/payments', label: 'Payments' },
];

export default function OperatorLayout() {
  const { principal } = usePortalPrincipal();
  const name = principal?.operator_name || '';
  const { pathname } = useLocation();
  const isUnder = ({ alsoActiveUnder }) => Boolean(
    alsoActiveUnder && pathname.startsWith(alsoActiveUnder),
  );

  return (
    <div className="pp-op">
      <header className="pp-op__bar">
        <div className="pp-op__bar-left">
          <BrandLogo variant="white" height={34} />
        </div>
        {/* A sibling of the brand and of "who", not nested in either: on a
            phone the tabs take a row of their own (Operator.scss), which is
            what keeps the logo and Sign out on one line together. */}
        <nav className="pp-op__nav" aria-label="Operator sections">
          {OPERATOR_SECTIONS.map((section) => (
            <NavLink
              key={section.to}
              to={section.to}
              end={section.end}
              className={({ isActive }) => `pp-op__tab${isActive || isUnder(section) ? ' is-active' : ''}`}
            >
              {section.label}
            </NavLink>
          ))}
        </nav>
        <div className="pp-op__who">
          {name ? <span className="pp-op__who-name">{name}</span> : null}
          <span className="pp-op__avatar" aria-hidden="true">{initials(name) || 'OP'}</span>
          <button type="button" className="pp-op__signout" onClick={signOutOfPortal}>
            Sign out
          </button>
        </div>
      </header>

      <main className="pp-op__main">
        <Outlet />
      </main>
    </div>
  );
}
